import { flow } from 'fp-ts/function';
import { __, compact, constant, flatMap, flatten, get, includes, isEmpty, map, rest } from 'lodash/fp';
import { createSelector } from 'reselect';

import { CheckoutField } from '@mews-distributor/common';
import { getBooleanProp, getOrEmptyObject, getStringProp } from '@mews/fp';

import {
    getRequiredFields,
    isBookerReservationCustomerEmailFieldRequired,
} from '../../../../modules/configuration/selectors';
import {
    getSelectedEnterpriseSupportedPaymentCardTypes,
    hasSelectedEnterprisePciProxyStorage,
} from '../../../../selectors/views/selectedEnterprisePaymentConfiguration';
import { pciProxy } from '../../../../services/pciProxy';
import { ApplicationState } from '../../../../types';
import {
    baseRequiredFields,
    configurableRequiredFields,
    getBookerConstraints,
    getCheckoutFormConstraints,
    getCreditCardFormConstraints,
    getPciProxyPaymentCardConstraints,
} from '../../../../utils/constraints';
import { getCheckout, getCustomerEmail, getCustomerValues, isBookerEnabled } from '../../data/selectors';
import { shouldDisplayPaymentCardForm } from '../../data/viewSelectors';
import {
    getBookerSchema,
    getCheckoutFormSchema,
    getCreditCardFormSchema,
    getPciProxyPaymentCardSchema,
} from '../../../../utils/schemas';
import { CheckoutCustomerFormValues } from './types';

export const hasCheckoutFailed = flow(getCheckout, get('hasFailed'), Boolean);
export const isCheckoutInProgress = flow(getCheckout, get('isCheckoutInProgress'), Boolean);
export const hasCheckoutPreparationFailed = flow(getCheckout, get('hasPreparationFailed'), Boolean);

export const getCurrentPaymentCardType = flow(getCheckout, get('currentPaymentCardType'));

const getCheckoutStatusTextKey = flow(getCheckout, get('statusTextKey'));

export const getCheckoutStatus = createSelector(getCheckoutStatusTextKey, (statusTextKey) => ({ statusTextKey }));

const getCustomerErrors = flow(getCheckout, getOrEmptyObject('customerErrors'));
const getFields = flow(getCheckout, getOrEmptyObject('fields'));

export const getCustomerValue = <K extends keyof CheckoutCustomerFormValues>(
    state: ApplicationState,
    { name, defaultValue }: { name: K; defaultValue?: CheckoutCustomerFormValues[K] }
) => {
    const values = getCustomerValues(state);
    return values[name] ?? defaultValue;
};

const getField = createSelector(getStringProp('name'), getFields, get);

export const isFieldTouched = flow(getField, get('touched'), Boolean);

export const areAllFieldsTouched = flow(getCheckout, get('allFieldsTouched'), Boolean);

export const getCustomerError = createSelector(getStringProp('name'), getCustomerErrors, get);

const getCheckoutField = get(__, CheckoutField);
const getFormattedRequiredFields = flow(getRequiredFields, map(getCheckoutField));

const getConfiguredCheckoutRequiredFields = createSelector(
    getFormattedRequiredFields,
    flatMap(get(__, configurableRequiredFields))
);

const getConfiguredGuestEmailField = createSelector(
    isBookerEnabled,
    isBookerReservationCustomerEmailFieldRequired,
    (isBooker, isBookerReservationCustomerEmailRequired) =>
        !isBooker || (isBooker && isBookerReservationCustomerEmailRequired) ? CheckoutField.Email : null
);

export const getCheckoutRequiredFields = createSelector(
    constant(baseRequiredFields),
    getConfiguredCheckoutRequiredFields,
    getConfiguredGuestEmailField,
    flow(rest(flatten), compact)
);

export const isCheckoutFieldRequired = createSelector(getStringProp('name'), getCheckoutRequiredFields, includes);

export const hasCustomerErrors = createSelector(getCustomerErrors, (errors) => !isEmpty(errors));

const { getCardNumberMetadata, getCvvMetadata } = pciProxy.selectors;

export const getCustomerValuesConstraints = createSelector(
    shouldDisplayPaymentCardForm,
    getSelectedEnterpriseSupportedPaymentCardTypes,
    hasSelectedEnterprisePciProxyStorage,
    getCardNumberMetadata,
    getCvvMetadata,
    getCustomerEmail,
    isBookerEnabled,
    getCheckoutRequiredFields,
    getBooleanProp('isPhoneInputPrototypeEnabled'),
    (
        hasPaymentCardForm,
        supportedCreditCardTypes,
        hasPciProxyStorage,
        cardNumberMeta,
        cvvMeta,
        customerEmail,
        bookerEnabled,
        requiredFields,
        isPhoneInputPrototypeEnabled
    ) =>
        !hasPaymentCardForm
            ? {
                  ...getCheckoutFormConstraints({ requiredFields, isPhoneInputPrototypeEnabled }),
                  ...(bookerEnabled
                      ? getBookerConstraints({ customerEmail, isPhoneInputPrototypeEnabled }, requiredFields)
                      : {}),
              }
            : {
                  ...getCheckoutFormConstraints({ requiredFields, isPhoneInputPrototypeEnabled }),
                  ...(hasPciProxyStorage
                      ? getPciProxyPaymentCardConstraints({
                            number: cardNumberMeta,
                            cvv: cvvMeta,
                            supportedCreditCardTypes,
                        })
                      : getCreditCardFormConstraints({
                            supportedCreditCardTypes,
                        })),
                  ...(bookerEnabled
                      ? getBookerConstraints({ customerEmail, isPhoneInputPrototypeEnabled }, requiredFields)
                      : {}),
              }
);

export const getCustomerValuesSchema = createSelector(
    shouldDisplayPaymentCardForm,
    getSelectedEnterpriseSupportedPaymentCardTypes,
    hasSelectedEnterprisePciProxyStorage,
    getCustomerEmail,
    isBookerEnabled,
    getCheckoutRequiredFields,
    (
        hasPaymentCardForm,
        supportedCreditCardTypes,
        hasPciProxyStorage,
        customerEmail,
        bookerEnabled,
        requiredFields
    ) => {
        const isPhoneRequired = requiredFields.includes(CheckoutField.Telephone);
        const isEmailRequired = requiredFields.includes(CheckoutField.Email);

        const checkoutFormSchema = getCheckoutFormSchema({ isPhoneRequired, isEmailRequired });
        const bookerSchema = getBookerSchema({ customerEmail: customerEmail as string, isPhoneRequired });

        let finalSchema = checkoutFormSchema;

        if (bookerEnabled) {
            finalSchema = finalSchema.merge(bookerSchema);
        }

        if (hasPaymentCardForm) {
            const pciProxyPaymentCardSchema = getPciProxyPaymentCardSchema(supportedCreditCardTypes);
            const creditCardFormSchema = getCreditCardFormSchema(supportedCreditCardTypes);

            finalSchema = finalSchema.merge(hasPciProxyStorage ? pciProxyPaymentCardSchema : creditCardFormSchema);
        }

        return finalSchema;
    }
);
